import React, { useState } from 'react';

import _ from 'lodash';
import axios, { AxiosError, AxiosResponse } from 'axios';
import tinycolor from 'tinycolor2';

import { OrganizationBrandData } from '../app/types';

import BrandContext from './context';
import { BrandValue } from './types';

const BrandProvider = (props): JSX.Element => {
  const [isCoBranded, setIsCobranded] = useState<boolean>();
  const [coBrandUrl, setCoBrandUrl] = useState<string>();
  const defaultColors = {
    light: 'rgb(246, 250, 252)',
    primary: 'rgb(10, 115, 186)',
    dark: 'rgb(5, 69, 111)',
  };
  const [primaryBrand, setPrimaryBrand] = useState<OrganizationBrandData>({
    id: -1,
    name: '',
    placement: 'primary',
    colors: defaultColors,
  });
  const [coBrand, setCoBrand] = useState<OrganizationBrandData>({
    id: -1,
    name: '',
    placement: 'secondary',
    colors: defaultColors,
  });
  const lbBrand: OrganizationBrandData = {
    id: 0,
    name: 'Lightbeam Health Solutions',
    placement: 'secondary',
    colors: defaultColors,
    logoUrl: 'https://lb-jcl.s3.us-west-2.amazonaws.com/logos/1666920972815-Lightbeam_Logo_-_Color.jpg',
    logoHeight: 125,
    logoWidth: 500,
  };

  function generateBrandColors(color: string) {
    const primary = (color as string);
    const hue = tinycolor(primary).toHsl().h;
    const light = `hsl(${hue}, 100%, 97%)`;
    const dark = `hsl(${hue}, 100%, 8%)`;
    // Assign colors
    return {
      light: tinycolor(light).toHslString(),
      primary: tinycolor(primary).toHslString(),
      dark: tinycolor(dark).toHslString(),
    };
  }

  const validCoBrands = {
    // IDs in remote Organization table
    // TODO: Store thid data within separate CoBrands table to stream line deployment
    'amga': 40,
    'cardinal': 41,
    'cerner': 42,
    'deloitte': 43,
    'flaacos': 44,
    'hch': 45,
    'iks': 46,
    'klas': 47,
    'microsoft': 48,
    'mtc_chicago': 48,
    'mpg': 49,
    'navvis': 50,
    'rti': 51,
    'stratum': 52,
    'summit': 70,
    'tachc': 53,
  };

  async function fetchCoBrand (brandName: string) {
    if (brandName && _.indexOf(_.keys(validCoBrands), brandName.toLowerCase()) > -1) {
      const orgId = validCoBrands[brandName];
      setCoBrandUrl(brandName);
      await axios
        .get(`/data_organization/${orgId}/`)
        .then((res: AxiosResponse) => {
          if (res.data.status === 'OK') {
            const orgData = res.data.organization;
            const brand: OrganizationBrandData = {
              id: orgData.id,
              name: orgData.name,
              placement: orgData.type,
              colors: generateBrandColors(orgData.brand_color),
              logoUrl: orgData.logo_url,
              logoHeight: orgData.logo_height,
              logoWidth: orgData.logo_width,
            };
            setCoBrand(brand);
            setIsCobranded(true);
          }
        })
        .catch((err: AxiosError) => {
          console.error(err);
        });
    } else {
      setCoBrandUrl(null);
      return false;
    }
  }

  function appBrandTitle () {
    return (isCoBranded)
      ? `radian™ for ${coBrand.name}`
      : 'radian™'
    ;
  }

  function isValidBrand (brand: OrganizationBrandData): boolean {
    return (brand && brand.id !== -1);
  }

  function getColors (): OrganizationBrandData['colors'] {
    if (isCoBranded && isValidBrand(coBrand) && !primaryBrand.logoUrl) {
      return coBrand.colors;
    } else if (primaryBrand.logoUrl) {
      return primaryBrand.colors;
    } else {
      return defaultColors;
    }
  }

  const value: BrandValue = {
    isCoBranded,
    appBrandTitle: appBrandTitle(),
    defaultColors,
    primaryBrand,
    coBrand,
    coBrandUrl,
    lbBrand,
    isValidBrand,
    getColors,
    fetchCoBrand,
    setCoBrandUrl,
    setPrimaryBrand,
  };
  return <BrandContext.Provider value={value} {...props} />;
};

export default BrandProvider;
